import { useWatch } from 'react-hook-form';
import { Element as TargetToScroll } from 'react-scroll';
import { useTranslation } from 'react-i18next';
import { useRootSelector } from '../../../../../../../store/hooks';
import { getSecondaryProductsWithProductType } from '../../../../../helpers/selectPackage/getSecondaryProductsWithProductType';
import { getRelatedPrimaryProductsData } from '../../../../../helpers/selectPackage/getRelatedPrimaryProducts';
import { selectSitePricingProductsData } from '../../../../../store/features/products';
import { selectSitePricingPackagesStatus } from '../../../../../store/features/packages';
import { PricingPackagesFormValues } from '../../../../../store/features/packages/types';
import { PricingProduct } from '../../../../../store/features/products/types';
import { SelectionProductId } from '../../../../../hooks/selectPackage/usePackageSelectionHandlers';
import { MuiStack } from '../../../../../../../modules/material';
import ProductsListWrapper from '../shared/ProductsListWrapper';
import ProductBadge from '../shared/ProductBadge';
import PackageSummaryPrice from '../shared/PackageSummaryPrice';
import AddToBasketButton from '../shared/AddToBasketButton';
import PrimaryProductCard from '../PrimaryProductCard';
import ProductOptions from '../ProductOptions';
import RelatedPrimaryProducts from '../RelatedPrimaryProducts';
import SecondaryProducts from '../SecondaryProducts';
import CardCollapse from '../shared/CardCollapse';
import { StyledMobileOnly } from './styles';

export interface PrimaryProductsGroupProps {
  products: PricingProduct[];
  onClickPrimaryProduct: (
    isSecondLevelAvailable: boolean,
    product: PricingProduct,
    productIndex: number,
  ) => void;
  onCollapsePrimaryProduct: (
    product: PricingProduct,
    productIndex: number,
  ) => void;
  isFirstProductsGroup?: boolean;
  isShownProductType?: boolean;
  selectedProductId?: SelectionProductId;
  addedToBasketProductId?: SelectionProductId;
}

export default function PrimaryProductsGroup(props: PrimaryProductsGroupProps) {
  const { t } = useTranslation();
  const {
    products,
    onClickPrimaryProduct,
    onCollapsePrimaryProduct,
    isFirstProductsGroup,
    isShownProductType,
    selectedProductId,
    addedToBasketProductId,
  } = props;

  const pricingPackageStatus = useRootSelector(selectSitePricingPackagesStatus);
  const { productTypesWithProducts, optionalProductTypesWithProducts } =
    useRootSelector(selectSitePricingProductsData);
  const isPackageLoading = pricingPackageStatus === 'pending';
  const productMultipleIds = useWatch<PricingPackagesFormValues>({
    name: 'productsSelections.productMultipleIds',
  }) as PricingPackagesFormValues['productsSelections']['productMultipleIds'];

  return (
    <>
      {products.map((product, productIndex) => {
        const {
          id,
          productTypeId,
          productOptionsList,
          featuredLabelEnabled,
          featuredLabel,
          featuredColorHex,
          featuredIconId,
        } = product;
        const isProductAddedToBasket = addedToBasketProductId === id;
        const isSecondLevelActive = selectedProductId === id;
        const isFirstProduct = isFirstProductsGroup && productIndex === 0;
        const secondaryProductsWithProductTypes =
          getSecondaryProductsWithProductType(
            id,
            isSecondLevelActive,
            optionalProductTypesWithProducts,
            productMultipleIds,
          );
        const {
          products: relatedPrimaryProducts,
          name: relatedPrimaryProductsTitle,
        } = getRelatedPrimaryProductsData(
          productTypesWithProducts,
          productTypeId,
          id,
        );

        const isProductOptions = productOptionsList.length > 0;
        const isRelatedPrimaryProducts = relatedPrimaryProducts.length > 0;
        const isSecondaryProducts =
          secondaryProductsWithProductTypes.length > 0;
        // Second level is available only if the Primary product has one of the following:
        // - product options
        // - related primary products (in cases multiple choices product type group)
        // - related secondary products
        const isSecondLevelAvailable =
          isProductOptions || isRelatedPrimaryProducts || isSecondaryProducts;

        const handleOnClickPrimaryProduct = () => {
          onClickPrimaryProduct(isSecondLevelAvailable, product, productIndex);
        };
        const handleCollapsePrimaryProduct = () =>
          onCollapsePrimaryProduct(product, productIndex);

        return (
          <ProductsListWrapper
            isFirstProduct={isFirstProduct}
            isShownProductType={isShownProductType}
            isSecondLevelActive={isSecondLevelActive}
            badge={
              featuredLabelEnabled && (
                <ProductBadge
                  label={featuredLabel}
                  hexColor={featuredColorHex}
                  iconId={featuredIconId}
                />
              )
            }
            key={id}
          >
            <TargetToScroll name={id}>
              <PrimaryProductCard
                product={product}
                isSecondLevelActive={isSecondLevelActive}
                data-testid="pricing-primary-product-card"
              >
                <AddToBasketButton
                  type="submit"
                  color="primary"
                  onClick={handleOnClickPrimaryProduct}
                  enableAddedToBasketView={isProductAddedToBasket}
                  disabled={isPackageLoading}
                  fullWidth
                  className={isSecondLevelAvailable ? '' : 'box__basket-submit'}
                >
                  {isSecondLevelAvailable &&
                    t(
                      'site.pricing.products-selection.primary-product-card.button.select.label',
                    )}
                </AddToBasketButton>
              </PrimaryProductCard>
            </TargetToScroll>

            {isSecondLevelActive && (
              <MuiStack spacing={2.5} sx={{ mt: 2.5 }}>
                <StyledMobileOnly>
                  <CardCollapse onClick={handleCollapsePrimaryProduct} />
                </StyledMobileOnly>

                {isProductOptions && (
                  <ProductOptions options={productOptionsList} />
                )}

                {isRelatedPrimaryProducts && (
                  <RelatedPrimaryProducts
                    products={relatedPrimaryProducts}
                    title={relatedPrimaryProductsTitle}
                  />
                )}

                {isSecondaryProducts &&
                  secondaryProductsWithProductTypes.map(
                    ({ secondaryProducts, optionalProductType }) => (
                      <SecondaryProducts
                        key={optionalProductType.productTypeId}
                        products={secondaryProducts}
                        name={optionalProductType.name}
                        selectionType={optionalProductType.selectionType}
                        productTypeId={optionalProductType.productTypeId}
                      />
                    ),
                  )}

                <StyledMobileOnly>
                  <CardCollapse onClick={handleCollapsePrimaryProduct} />
                </StyledMobileOnly>

                <PackageSummaryPrice
                  enableAddedToBasketView={isProductAddedToBasket}
                  disabled={isPackageLoading}
                  onCollapse={handleCollapsePrimaryProduct}
                />
              </MuiStack>
            )}
          </ProductsListWrapper>
        );
      })}
    </>
  );
}
